import { useEffect, useMemo, useState } from 'react';
import swal from 'sweetalert';
import {
  addInvoiceReceipt,
  approvePumpDraftInvoice,
  createPumpDraftInvoice,
  deleteInvoiceReceipt,
  fetchClientAccountSummary,
  fetchPumpInvoiceCustomers,
  fetchPumpInvoices,
  updatePumpDraftInvoice,
} from './adminApi';
import { INDIAN_GST_STATES, PAYMENT_MODES } from '../../../constants/indianGstStates';
import { INVOICE_SOURCES, SERVICE_TEMPLATES, SOURCE_BADGE_CLASS } from '../../../constants/petrosmartBilling';
import { formatInrAmount } from '../../../utils/reportFormat';
import { NumTd, NumTh } from '../../../utils/reportDisplay';

const EMPTY_LINE = { description: '', quantity: 1, rate: 0, hsn: '', unit: 'Nos', gst: 18 };

const WORKFLOW_LABELS = {
  draft: 'Draft',
  pending_approval: 'Pending approval',
  pending_payment: 'Pending payment',
  approved: 'Approved',
};

function todayDateString() {
  return new Date().toISOString().slice(0, 10);
}

const AdminPumpInvoicesPanel = ({ pumpId, defaultPlaceCode = '', defaultPlaceName = '' }) => {
  const [invoices, setInvoices] = useState([]);
  const [account, setAccount] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [receiptInvoice, setReceiptInvoice] = useState(null);
  const [form, setForm] = useState({
    op_date: todayDateString(),
    customer_id: '',
    invoice_source: 'custom',
    place_of_supply_code: defaultPlaceCode,
    place_of_supply_name: defaultPlaceName,
    discount: 0,
    note: '',
    line_items: [{ ...EMPTY_LINE }],
  });
  const [newReceipt, setNewReceipt] = useState({
    receipt_date: todayDateString(),
    payment_mode: 'cash',
    amount: '',
    paid_by: '',
    reference_no: '',
    note: '',
  });

  const totalAmount = useMemo(() => {
    const subtotal = form.line_items.reduce(
      (sum, line) => sum + Number(line.quantity || 0) * Number(line.rate || 0),
      0
    );
    return Math.max(0, subtotal - Number(form.discount || 0));
  }, [form.line_items, form.discount]);

  function loadData() {
    setLoading(true);
    Promise.all([
      fetchPumpInvoices(pumpId, filter || undefined),
      fetchClientAccountSummary(pumpId),
    ])
      .then(([invRes, accRes]) => {
        setInvoices(invRes.invoices || []);
        if (accRes.status) setAccount(accRes);
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    loadData();
    fetchPumpInvoiceCustomers(pumpId).then((res) => setCustomers(res.customers || []));
  }, [pumpId, filter]);

  useEffect(() => {
    if (defaultPlaceCode) {
      setForm((prev) => ({
        ...prev,
        place_of_supply_code: prev.place_of_supply_code || defaultPlaceCode,
        place_of_supply_name: prev.place_of_supply_name || defaultPlaceName,
      }));
    }
  }, [defaultPlaceCode, defaultPlaceName]);

  function resetForm() {
    setEditingId(null);
    setShowForm(false);
    setForm({
      op_date: todayDateString(),
      customer_id: '',
      invoice_source: 'custom',
      place_of_supply_code: defaultPlaceCode,
      place_of_supply_name: defaultPlaceName,
      discount: 0,
      note: '',
      line_items: [{ ...EMPTY_LINE }],
    });
  }

  function applyServiceTemplate(source) {
    const template = SERVICE_TEMPLATES[source];
    if (!template) return;
    setForm((prev) => ({
      ...prev,
      invoice_source: source,
      line_items: [{
        description: template.description,
        quantity: 1,
        rate: 0,
        hsn: template.hsn,
        unit: template.unit,
        gst: template.gst,
      }],
    }));
  }

  function openEdit(invoice) {
    setEditingId(invoice.id);
    setShowForm(true);
    setForm({
      op_date: invoice.op_date?.slice(0, 10) || todayDateString(),
      customer_id: invoice.customer_id || '',
      invoice_source: invoice.invoice_source || 'custom',
      place_of_supply_code: invoice.place_of_supply_code || defaultPlaceCode,
      place_of_supply_name: invoice.place_of_supply_name || defaultPlaceName,
      discount: invoice.discount || 0,
      note: invoice.note || '',
      line_items: invoice.line_items?.length ? invoice.line_items : [{ ...EMPTY_LINE }],
    });
  }

  function updateLine(index, field, value) {
    setForm((prev) => {
      const line_items = [...prev.line_items];
      line_items[index] = { ...line_items[index], [field]: value };
      return { ...prev, line_items };
    });
  }

  function addLine() {
    setForm((prev) => ({ ...prev, line_items: [...prev.line_items, { ...EMPTY_LINE }] }));
  }

  function removeLine(index) {
    setForm((prev) => ({
      ...prev,
      line_items: prev.line_items.filter((_, i) => i !== index),
    }));
  }

  function handlePlaceChange(code) {
    const state = INDIAN_GST_STATES.find((s) => s.code === code);
    setForm((prev) => ({
      ...prev,
      place_of_supply_code: code,
      place_of_supply_name: state?.name || '',
    }));
  }

  function saveDraft() {
    const payload = {
      ...form,
      customer_id: form.customer_id || null,
      discount: Number(form.discount || 0),
      payment_mode: 'credit',
      amount_received: 0,
    };

    const request = editingId
      ? updatePumpDraftInvoice(pumpId, editingId, payload)
      : createPumpDraftInvoice(pumpId, payload);

    request
      .then((res) => {
        if (res.status) {
          swal('Saved', res.message, 'success');
          resetForm();
          loadData();
        } else {
          swal('Error', res.message || 'Save failed', 'error');
        }
      })
      .catch((err) => swal('Error', err.message, 'error'));
  }

  function approveInvoice(invoiceId) {
    swal({ title: 'Approve this invoice?', icon: 'warning', buttons: true }).then((ok) => {
      if (!ok) return;
      approvePumpDraftInvoice(pumpId, invoiceId)
        .then((res) => {
          swal('Approved', res.message, 'success');
          loadData();
        })
        .catch((err) => swal('Error', err.message, 'error'));
    });
  }

  function openReceipts(invoice) {
    setReceiptInvoice(invoice);
    setNewReceipt({
      receipt_date: todayDateString(),
      payment_mode: 'cash',
      amount: invoice.amount_pending > 0 ? String(invoice.amount_pending) : '',
      paid_by: invoice.paid_by || invoice.customer_name || '',
      reference_no: '',
      note: '',
    });
  }

  function saveNewReceipt() {
    if (!receiptInvoice) return;
    const amount = Number(newReceipt.amount || 0);
    if (amount <= 0) {
      swal('Invalid amount', 'Enter a receipt amount greater than zero', 'warning');
      return;
    }

    addInvoiceReceipt(pumpId, receiptInvoice.id, {
      ...newReceipt,
      amount,
    })
      .then((res) => {
        if (res.status) {
          swal('Saved', res.message, 'success');
          if (res.invoice) setReceiptInvoice(res.invoice);
          loadData();
        } else {
          swal('Error', res.message || 'Save failed', 'error');
        }
      })
      .catch((err) => swal('Error', err.message, 'error'));
  }

  function removeReceipt(receiptId) {
    if (!receiptInvoice) return;
    swal({ title: 'Remove this receipt?', icon: 'warning', buttons: true }).then((ok) => {
      if (!ok) return;
      deleteInvoiceReceipt(pumpId, receiptInvoice.id, receiptId)
        .then((res) => {
          if (res.status) {
            if (res.invoice) setReceiptInvoice(res.invoice);
            loadData();
          }
        })
        .catch((err) => swal('Error', err.message, 'error'));
    });
  }

  const summary = account?.summary;

  return (
    <div className="mt-2">
      <h5 className="mb-3">Client accounting</h5>

      {summary && (
        <div className="row g-2 mb-4">
          <div className="col-md-3">
            <div className="admin-stat-card p-3 border rounded">
              <div className="small text-muted">Total billed</div>
              <strong className="report-amount d-block">{formatInrAmount(summary.total_billed)}</strong>
            </div>
          </div>
          <div className="col-md-3">
            <div className="admin-stat-card p-3 border rounded">
              <div className="small text-muted">Total received</div>
              <strong className="text-success report-amount d-block">{formatInrAmount(summary.total_received)}</strong>
            </div>
          </div>
          <div className="col-md-3">
            <div className="admin-stat-card p-3 border rounded">
              <div className="small text-muted">Outstanding</div>
              <strong className="text-danger report-amount d-block">{formatInrAmount(summary.total_pending)}</strong>
            </div>
          </div>
          <div className="col-md-3">
            <div className="admin-stat-card p-3 border rounded">
              <div className="small text-muted">Drafts / invoices</div>
              <strong>{summary.draft_count} draft · {summary.invoice_count} total</strong>
            </div>
          </div>
        </div>
      )}

      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="mb-0">Invoices & receipt ledger</h6>
        <button className="btn btn-sm btn-primary" onClick={() => { setShowForm(true); setEditingId(null); }}>
          + New draft invoice
        </button>
      </div>

      <div className="btn-group btn-group-sm mb-3">
        {['', 'draft', 'pending', 'approved'].map((key) => (
          <button
            key={key || 'all'}
            type="button"
            className={`btn btn-outline-secondary ${filter === key ? 'active' : ''}`}
            onClick={() => setFilter(key)}
          >
            {key === '' ? 'All' : key === 'pending' ? 'Pending payment' : key.charAt(0).toUpperCase() + key.slice(1)}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-muted">Loading...</p>
      ) : invoices.length === 0 ? (
        <p className="text-muted">No invoices yet. Add subscription or create a service invoice draft.</p>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm table-bordered align-middle">
            <thead className="table-light">
              <tr>
                <th>#</th>
                <th>Date</th>
                <th>Type</th>
                <NumTh>Total</NumTh>
                <NumTh>Discount</NumTh>
                <NumTh>Received</NumTh>
                <NumTh>Pending</NumTh>
                <th>Receipts</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <tr key={inv.id}>
                  <td>{inv.id}</td>
                  <td>{inv.op_date?.slice(0, 10)}</td>
                  <td>
                    <span className={`badge ${SOURCE_BADGE_CLASS[inv.invoice_source] || 'bg-secondary'}`}>
                      {inv.invoice_source_label || inv.invoice_source}
                    </span>
                  </td>
                  <NumTd>{inv.total}</NumTd>
                  <NumTd>{inv.discount || 0}</NumTd>
                  <NumTd>{inv.amount_received}</NumTd>
                  <NumTd value={inv.amount_pending} className={inv.amount_pending > 0 ? 'text-danger fw-semibold' : ''} />
                  <td>{(inv.receipts || []).length}</td>
                  <td>
                    <span className={`badge ${inv.is_draft ? 'bg-secondary' : inv.amount_pending > 0 ? 'bg-warning text-dark' : 'bg-success'}`}>
                      {WORKFLOW_LABELS[inv.workflow] || inv.workflow}
                    </span>
                  </td>
                  <td className="text-nowrap">
                    {inv.is_draft && (
                      <>
                        <button type="button" className="btn btn-link btn-sm p-0 me-2" onClick={() => openEdit(inv)}>Edit</button>
                        <button type="button" className="btn btn-link btn-sm p-0 me-2" onClick={() => approveInvoice(inv.id)}>Approve</button>
                      </>
                    )}
                    {!inv.is_draft && (
                      <button type="button" className="btn btn-link btn-sm p-0 me-2" onClick={() => openReceipts(inv)}>Receipts</button>
                    )}
                    {!inv.is_draft && inv.print_url && (
                      <a className="btn btn-link btn-sm p-0" href={inv.print_url} target="_blank" rel="noopener noreferrer">Print</a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <div className="admin-panel border rounded p-3 mb-3">
          <h6>{editingId ? `Edit draft #${editingId}` : 'New draft invoice'}</h6>

          <div className="mb-2">
            <span className="small text-muted me-2">Quick templates:</span>
            {['consulting', 'data_entry', 'reimbursement'].map((key) => (
              <button
                key={key}
                type="button"
                className="btn btn-sm btn-outline-secondary me-1 mb-1"
                onClick={() => applyServiceTemplate(key)}
              >
                {INVOICE_SOURCES.find((s) => s.value === key)?.label}
              </button>
            ))}
          </div>

          <div className="row g-2">
            <div className="col-md-2">
              <label className="form-label">Invoice date</label>
              <input type="date" className="form-control" value={form.op_date} onChange={(e) => setForm({ ...form, op_date: e.target.value })} />
            </div>
            <div className="col-md-3">
              <label className="form-label">Invoice type</label>
              <select className="form-select" value={form.invoice_source} onChange={(e) => setForm({ ...form, invoice_source: e.target.value })}>
                {INVOICE_SOURCES.filter((s) => s.value !== 'subscription').map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Place of supply</label>
              <select className="form-select" value={form.place_of_supply_code} onChange={(e) => handlePlaceChange(e.target.value)}>
                <option value="">Select...</option>
                {INDIAN_GST_STATES.map((s) => (
                  <option key={s.code} value={s.code}>{s.code} - {s.name}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label">Discount (₹)</label>
              <input type="number" className="form-control" value={form.discount} onChange={(e) => setForm({ ...form, discount: e.target.value })} />
            </div>
            <div className="col-md-2">
              <label className="form-label">Invoice total</label>
              <input className="form-control bg-light report-amount" readOnly value={formatInrAmount(totalAmount, { symbol: false })} />
            </div>
            <div className="col-md-12">
              <label className="form-label">Notes</label>
              <input className="form-control" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
            </div>
          </div>

          <h6 className="mt-3">Line items</h6>
          {form.line_items.map((line, index) => (
            <div className="row g-2 mb-2" key={index}>
              <div className="col-md-4">
                <input className="form-control" placeholder="Description" value={line.description} onChange={(e) => updateLine(index, 'description', e.target.value)} />
              </div>
              <div className="col-md-1">
                <input type="number" className="form-control" placeholder="Qty" value={line.quantity} onChange={(e) => updateLine(index, 'quantity', e.target.value)} />
              </div>
              <div className="col-md-2">
                <input type="number" className="form-control" placeholder="Rate" value={line.rate} onChange={(e) => updateLine(index, 'rate', e.target.value)} />
              </div>
              <div className="col-md-2">
                <input className="form-control" placeholder="HSN" value={line.hsn || ''} onChange={(e) => updateLine(index, 'hsn', e.target.value)} />
              </div>
              <div className="col-md-2">
                <input type="number" className="form-control" placeholder="GST %" value={line.gst ?? 18} onChange={(e) => updateLine(index, 'gst', e.target.value)} />
              </div>
              <div className="col-md-1">
                <button type="button" className="btn btn-outline-danger btn-sm w-100" onClick={() => removeLine(index)} disabled={form.line_items.length === 1}>×</button>
              </div>
            </div>
          ))}
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={addLine}>+ Add line</button>

          <p className="small text-muted mt-2">Receipts are recorded after approval — multiple payments with dates are supported.</p>

          <div className="mt-3">
            <button type="button" className="btn btn-primary me-2" onClick={saveDraft}>Save draft</button>
            <button type="button" className="btn btn-outline-secondary" onClick={resetForm}>Cancel</button>
          </div>
        </div>
      )}

      {receiptInvoice && (
        <div className="admin-panel border rounded p-3 mb-3">
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <h6>Receipt ledger — Invoice #{receiptInvoice.id}</h6>
              <p className="small text-muted mb-0">
                Total {formatInrAmount(receiptInvoice.total)} · Received {formatInrAmount(receiptInvoice.amount_received)} · Pending {formatInrAmount(receiptInvoice.amount_pending)}
              </p>
            </div>
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setReceiptInvoice(null)}>Close</button>
          </div>

          {(receiptInvoice.receipts || []).length > 0 ? (
            <table className="table table-sm mt-3 mb-3">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Mode</th>
                  <th>Paid by</th>
                  <th>Reference</th>
                  <th className="text-end">Amount</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {(receiptInvoice.receipts || []).map((r) => (
                  <tr key={r.id}>
                    <td>{r.receipt_date}</td>
                    <td>{r.payment_mode}</td>
                    <td>{r.paid_by || '-'}</td>
                    <td>{r.reference_no || '-'}</td>
                    <NumTd>{r.amount}</NumTd>
                    <td>
                      <button type="button" className="btn btn-link btn-sm text-danger p-0" onClick={() => removeReceipt(r.id)}>Remove</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="small text-muted mt-3">No receipts recorded yet.</p>
          )}

          <h6 className="mt-2">Add receipt</h6>
          <div className="row g-2">
            <div className="col-md-2">
              <label className="form-label">Receipt date</label>
              <input type="date" className="form-control" value={newReceipt.receipt_date} onChange={(e) => setNewReceipt({ ...newReceipt, receipt_date: e.target.value })} />
            </div>
            <div className="col-md-2">
              <label className="form-label">Mode</label>
              <select className="form-select" value={newReceipt.payment_mode} onChange={(e) => setNewReceipt({ ...newReceipt, payment_mode: e.target.value })}>
                {PAYMENT_MODES.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label">Amount (₹)</label>
              <input type="number" className="form-control" value={newReceipt.amount} onChange={(e) => setNewReceipt({ ...newReceipt, amount: e.target.value })} />
            </div>
            <div className="col-md-2">
              <label className="form-label">Paid by</label>
              <input className="form-control" value={newReceipt.paid_by} onChange={(e) => setNewReceipt({ ...newReceipt, paid_by: e.target.value })} />
            </div>
            <div className="col-md-2">
              <label className="form-label">Cheque / UPI ref</label>
              <input className="form-control" value={newReceipt.reference_no} onChange={(e) => setNewReceipt({ ...newReceipt, reference_no: e.target.value })} />
            </div>
            <div className="col-md-2 d-flex align-items-end">
              <button type="button" className="btn btn-primary w-100" onClick={saveNewReceipt}>Add receipt</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPumpInvoicesPanel;
